import { useMemo } from "react";
import { useNewConnectionFees } from "./useNewConnectionFees";
import { useTariffs } from "./useTariffs";
import { NewConnectionFeesResponse, TariffsResponse } from "../types";

interface UseConnectionFeesAndTariffsParams {
  /**
   * Reference number for the connection request
   */
  referenceNumber: string;
  /**
   * Account type for tariff fetching
   */
  accountType: string;
  /**
   * Whether to auto-fetch data on mount
   */
  autoFetch?: boolean;
}

interface UseConnectionFeesAndTariffsResult {
  /**
   * Connection fees data
   */
  connectionFeesData: NewConnectionFeesResponse | null;
  /**
   * Tariffs data
   */
  tariffsData: TariffsResponse | null;
  /**
   * Extracted LNSP IDs from connection fees
   */
  lnspIds: string[];
  /**
   * Loading states
   */
  loading: {
    connectionFees: boolean;
    tariffs: boolean;
  };
  /**
   * Error states
   */
  errors: {
    connectionFees: Error | null;
    tariffs: string | null;
  };
  /**
   * Refetch functions
   */
  refetch: {
    connectionFees: () => Promise<void>;
    tariffs: () => Promise<void>;
  };
}

/**
 * Custom hook that fetches connection fees and then uses the lnspIds to fetch tariffs
 * @param params - Configuration for the hook
 * @returns Object containing both connection fees and tariffs data with loading/error states
 */
export const useConnectionFeesAndTariffs = (
  params: UseConnectionFeesAndTariffsParams
): UseConnectionFeesAndTariffsResult => {
  const { referenceNumber, accountType, autoFetch = true } = params;

  // Fetch connection fees
  const {
    data: connectionFeesData,
    loading: connectionFeesLoading,
    error: connectionFeesError,
    fetchFees: refetchConnectionFees,
  } = useNewConnectionFees({
    autoFetch,
    requestId: referenceNumber,
  });

  // Extract lnspIds from connection fees response
  const lnspIds = useMemo(() => {
    if (!connectionFeesData || connectionFeesData.length === 0) {
      return [];
    }

    const allLnspIds: string[] = [];

    connectionFeesData.forEach((fee) => {
      // Collect lnsp_ids from electricity connection fees
      if (fee.electricity?.lnsp_ids) {
        allLnspIds.push(...fee.electricity.lnsp_ids);
      }
      // Collect lnsp_ids from gas connection fees
      if (fee.gas?.lnsp_ids) {
        allLnspIds.push(...fee.gas.lnsp_ids);
      }
    });

    // Remove duplicates
    return [...new Set(allLnspIds)];
  }, [connectionFeesData]);

  // Fetch tariffs using the extracted lnspIds
  const {
    data: tariffsData,
    loading: tariffsLoading,
    error: tariffsError,
    refetch: refetchTariffs,
  } = useTariffs(
    accountType && lnspIds.length > 0
      ? {
          accountType,
          lnspIds,
        }
      : null
  );

  return {
    connectionFeesData,
    tariffsData,
    lnspIds,
    loading: {
      connectionFees: connectionFeesLoading,
      tariffs: tariffsLoading,
    },
    errors: {
      connectionFees: connectionFeesError,
      tariffs: tariffsError,
    },
    refetch: {
      connectionFees: () => refetchConnectionFees(referenceNumber),
      tariffs: refetchTariffs,
    },
  };
};
